import React, { useRef } from 'react';
import { withRouter } from 'react-router-dom';

import Chart from 'react-apexcharts';

import Spinner from '../UI/Spinner/Spinner';
import FilterListIcon from '@material-ui/icons/FilterList';
import { IconButton, Tooltip } from '@material-ui/core';

import SinResultados from '../../assets/images/SinResultados.png';
import IndicadorDeshabilitado from '../../assets/images/IndicadorDeshabilitado.png';
import classes from './Styles/BigChartCard.css';
import locales from '../../shared/locales';
import { storageMonedaSigno } from '../../shared/sessionData';
import { formatNumber } from '../../shared/formatNumber';
import { colorsChart } from '../../shared/colorsCharts';
import { enviarPorMailOption } from '../UI/EnviarChart/EnviarChartOptions';

const BarChart = (props) => {
  const chartRef = useRef(null);

  // Tamaños para el estado vacío (opcionales)
  const emptyHeight = props.emptyStateHeight ?? 260;
  const emptyMaxWidth = props.emptyStateMaxWidth ?? 360;

  const options = {
    chart: {
      defaultLocale: 'es',
      locales: locales,
      type: 'bar',
      stacked: props.chartStacked,
      toolbar: {
        show: true,
        // margen a la derecha/arriba para los botones
        offsetX: -12,
        offsetY: 6,
        tools: {
          download: true,
          customIcons: [enviarPorMailOption(chartRef, props.title)]
        }
      },
      zoom: {
        enabled: true,
        type: 'y',
        autoScaleYaxis: false,
        zoomedArea: {
          fill: { color: '#90CAF9', opacity: 0.4 },
          stroke: { color: '#0D47A1', opacity: 0.4, width: 1 }
        }
      },
      events: {
        dataPointSelection: function (event, chartContext, config) {
          if (props.seriesRedirectGetter) {
            const redirect = props.seriesRedirectGetter
              ? props.seriesRedirectGetter(config.seriesIndex, config.dataPointIndex)
              : '';
            props.history.push(redirect);
          }
          if (props.onPorcionClick) {
            props.onPorcionClick(config);
          }
        }
      },
      dropShadow: { enabled: true, color: '#000', top: 5, left: 3, blur: 7, opacity: 0.2 }
    },
    colors: props.colors ? props.colors : colorsChart,
    dataLabels: {
      enabled: props.dataLabelsEnabled,
      formatter: function (val, opts) {
        return props.dataLabelsFormatter ? props.dataLabelsFormatter(val, opts) : val;
      }
    },
    tooltip: {
      enabled: props.tooltipEnabled,
      custom: props.tooltipCustom
        ? function ({ series, seriesIndex, dataPointIndex, w }) {
            return props.tooltipCustom(series, seriesIndex, dataPointIndex, w);
          }
        : undefined
    },
    grid: { show: props.gridShow },
    responsive: [
      {
        breakpoint: 480,
        options: { legend: { position: 'bottom' } }
      }
    ],
    plotOptions: {
      bar: { horizontal: false, distributed: props.plotOptionsBarDistributed }
    },
    xaxis: {
      categories: props.categories,
      labels: { trim: true },
      tickPlacement: props.xaxisTickPlacement
    },
    yaxis: (() => {
      const moneyFmt = (val) => `${storageMonedaSigno()}${formatNumber(val)}`;

      if (props.dualY) {
        return [
          {
            // Eje izquierdo (para Monto)
            labels: { show: true, formatter: moneyFmt },
            min: props.y1Min ?? 0,
            max: props.y1Max,
            forceNiceScale: props.y1ForceNiceScale ?? true
          },
          {
            // Eje derecho (para Punto de equilibrio)
            opposite: true,
            labels: { show: true, formatter: props.y2Formatter || moneyFmt },
            min: props.y2Min ?? 0,
            max: props.y2Max,
            forceNiceScale: props.y2ForceNiceScale ?? true
          }
        ];
      }

      // Default: un solo eje
      return { labels: { show: true, formatter: moneyFmt } };
    })(),
    // Leyenda: por defecto a la derecha; si props.legendBottom === true, va abajo centrada
    legend: (() => {
      const base = { show: !props.disabledLegendShow };
      if (props.legendBottom) {
        return {
          ...base,
          position: 'bottom',
          horizontalAlign: 'center',
          offsetY: 0,
          markers: { width: 12, height: 12 },
          itemMargin: { horizontal: 8, vertical: 4 }
        };
      }
      // default (lado derecho)
      return { ...base, position: 'right', offsetY: 100 };
    })(),
    fill: { opacity: 1 }
  };

  return (
    <div
      ref={chartRef}
      style={{ height: '100%', width: '100%', boxShadow: props.noBoxShadow && 'none', position: 'relative' }}
      className={classes.BigChartCard}
    >
      <div style={{ display: 'flex', justifyContent: props.titleJustifyContent && props.titleJustifyContent }}>
        <div className={classes.TitleCard}>
          <div
            style={{
              fontWeight: 'bold',
              marginBottom: '1px',
              marginTop: '14px',
              width: '100%',
              display: 'flex',
              alignItems: props.alignTitleItems ? props.alignTitleItems : 'center',
              justifyContent: 'space-between'
            }}
          >
            {props.title}
          </div>
          {props.onFiltersClick && !props.disabled && (
            <Tooltip title="Ver filtros del indicador">
              <IconButton size="small" onClick={() => props.onFiltersClick()}>
                <FilterListIcon style={{ color: props.border && 'white' }} fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
        {props.children}
      </div>

      {props.disabled ? (
        <img alt="Indicador Deshabilitado" src={IndicadorDeshabilitado} />
      ) : props.loading ? (
        // Spinner centrado
        <div
          style={{
            minHeight: typeof props.height === 'number' ? props.height : 260,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Spinner />
        </div>
      ) : props.series.length > 0 ? (
        <Chart
          options={options}
          style={{ textAlign: props.chartTextAlign ? props.chartTextAlign : 'center' }}
          series={props.series}
          type="bar"
          height={props.height}
        />
      ) : (
        // Estado vacío controlado y centrado
        <div
          style={{
            height: emptyHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 12
          }}
        >
          <img
            alt="Sin Resultados"
            src={SinResultados}
            style={{ maxWidth: emptyMaxWidth, width: '100%', height: 'auto', opacity: 0.7 }}
          />
        </div>
      )}
    </div>
  );
};

export default withRouter(BarChart);
